"use client";

import { RiHeartFill, RiStarFill, RiStarLine } from "@remixicon/react";
import { memo, useMemo } from "react";
import { cn } from "../../lib/utils";
import { getDisplayAvatarUrl, getDisplayName } from "./renderers";
import type { FestivalInteraction, SetShowInteraction } from "./types";

/**
 * Common profile section component
 */
const ProfileSection = memo(
	({
		item,
		displayAvatar,
	}: {
		item: { display_name: string };
		displayAvatar: string | null;
	}) => (
		<div className="flex items-center gap-2">
			<div className="h-8 w-8 overflow-hidden rounded-full border border-border">
				{displayAvatar ? (
					<img
						src={displayAvatar}
						alt={item.display_name}
						className="h-full w-full object-cover"
					/>
				) : (
					<div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/20 to-primary/40">
						<span className="font-medium text-primary text-xs">
							{item.display_name.charAt(0).toUpperCase()}
						</span>
					</div>
				)}
			</div>
			<div className="font-medium text-popover-foreground text-sm">
				{getDisplayName(item as any)}
			</div>
		</div>
	),
);

ProfileSection.displayName = "ProfileSection";

/**
 * Star rating component
 */
const StarRating = memo(({ rating }: { rating: number }) => {
	const displayRating = rating / 2;
	const wholeStars = Math.floor(displayRating);
	const hasHalfStar = displayRating % 1 >= 0.5;

	return (
		<div className="flex items-center">
			{[...Array(5)].map((_, i) => {
				const starPosition = i + 1;

				if (starPosition <= wholeStars) {
					return (
						<RiStarFill
							key={`star-filled-${starPosition}`}
							className="h-3 w-3 text-yellow-500"
						/>
					);
				}
				if (starPosition === wholeStars + 1 && hasHalfStar) {
					return (
						<div key={`star-half-${starPosition}`} className="relative">
							<RiStarLine className="h-3 w-3 text-muted-foreground/50" />
							<div className="absolute top-0 left-0 w-[50%] overflow-hidden">
								<RiStarFill className="h-3 w-3 text-yellow-500" />
							</div>
						</div>
					);
				}
				return (
					<RiStarLine
						key={`star-empty-${starPosition}`}
						className="h-3 w-3 text-muted-foreground/50"
					/>
				);
			})}
		</div>
	);
});

StarRating.displayName = "StarRating";

/**
 * Comment section component
 */
const CommentSection = memo(({ content }: { content: string }) => (
	<div className="max-w-48 text-popover-foreground/80 text-xs italic">
		&quot;
		{content.length > 100 ? `${content.substring(0, 100)}...` : content}
		&quot;
	</div>
));

CommentSection.displayName = "CommentSection";

/**
 * Set/Show interaction card component
 */
export const SetShowInteractionCard = memo(
	({
		interaction,
		statusBadgeRenderer,
	}: {
		interaction: SetShowInteraction;
		statusBadgeRenderer?: (interaction: SetShowInteraction) => React.ReactNode;
	}) => {
		const displayAvatar = useMemo(
			() => getDisplayAvatarUrl(interaction),
			[interaction],
		);

		return (
			<div className="space-y-2">
				<ProfileSection item={interaction} displayAvatar={displayAvatar} />

				{/* Rating and Love */}
				{((interaction.rating && interaction.rating > 0) ||
					interaction.loved) && (
					<div className="flex items-center gap-1">
						{interaction.rating && interaction.rating > 0 && (
							<StarRating rating={interaction.rating} />
						)}
						{interaction.loved && (
							<RiHeartFill className="h-3 w-3 text-red-500" />
						)}
					</div>
				)}

				{/* Comment */}
				{interaction.content && (
					<CommentSection content={interaction.content} />
				)}

				{/* Status badge */}
				{statusBadgeRenderer && statusBadgeRenderer(interaction)}
			</div>
		);
	},
);

SetShowInteractionCard.displayName = "SetShowInteractionCard";

/**
 * Festival interaction card component
 */
export const FestivalInteractionCard = memo(
	({
		interaction,
		isPast,
		statusBadgeRenderer,
	}: {
		interaction: FestivalInteraction;
		isPast?: boolean;
		statusBadgeRenderer?: (
			interaction: FestivalInteraction,
			isPast?: boolean,
		) => React.ReactNode;
	}) => {
		const displayAvatar = useMemo(
			() => getDisplayAvatarUrl(interaction),
			[interaction],
		);

		return (
			<div className="space-y-2">
				<ProfileSection item={interaction} displayAvatar={displayAvatar} />

				{/* Festival Rating */}
				{interaction.festival_rating && interaction.festival_rating > 0 && (
					<div className="flex items-center gap-1">
						<StarRating rating={interaction.festival_rating} />
					</div>
				)}

				{/* Festival Review Comment */}
				{interaction.festival_content && (
					<CommentSection content={interaction.festival_content} />
				)}

				{/* Festival status badge */}
				{statusBadgeRenderer &&
					interaction.festival_status &&
					statusBadgeRenderer(interaction, isPast)}
			</div>
		);
	},
);

FestivalInteractionCard.displayName = "FestivalInteractionCard";
